import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import {
    IApiService,
    IAppResponse,
    ICoordinateSystemResponse,
    IGetCoordsByCN,
    IGetFiasDataRequest,
    IGetFiasDataResponse,
    IKeyPair,
    ILayerResponse,
    IMapSettings,
    IMarkerResponse,
    IObjectExportAsGeoRequest,
    IObjectExportRequest,
    IObjectListRequest,
    IObjectListResponse,
    IRenderMapFileRequest,
    IRenderMapFileResponse,
} from '@fui/map-library';

@Injectable()
export class ApiService implements IApiService {
    private readonly http: HttpClient = inject(HttpClient);

    private readonly apiPrefix = '/api/';

    removeMyElementFromMapSet(id: number): Promise<IAppResponse> {
        const url = `${this.apiPrefix}layers/layers_map_tree_my/remove`;
        return firstValueFrom(this.http.post<IAppResponse>(url, { id: id }));
    }

    fetchMyLayers(checkedLayers?: number[]): Observable<ILayerResponse> {
        const url = `${this.apiPrefix}layers/layers_api/get_my_layers`;
        return this.http.post<ILayerResponse>(url, { checkedLayers });
    }

    fetchPublicLayers(checkedLayers?: number[]): Observable<ILayerResponse> {
        const url = `${this.apiPrefix}layers/layers_api/get_my_layers`;
        return this.http.post<ILayerResponse>(url, { checkedLayers });
    }

    exportObjectData(request: IObjectExportRequest): Observable<HttpResponse<Blob>> {
        const url = `${this.apiPrefix}map/map_export/pdf_data`;
        return this.http.post<HttpResponse<Blob>>(url, request, {
            responseType: 'blob' as 'json',
            observe: 'response' as 'body',
        });
    }

    exportObjectAsGeoObject(request: IObjectExportAsGeoRequest): Observable<HttpResponse<Blob>> {
        const url = `${this.apiPrefix}export_layers/export`;
        return this.http.post<HttpResponse<Blob>>(url, request, {
            responseType: 'blob' as 'json',
            observe: 'response' as 'body',
        });
    }

    fetchObjectListData(request: IObjectListRequest): Observable<IAppResponse<IKeyPair<IObjectListResponse[]>>> {
        const url = `${this.apiPrefix}map/map_action/get_marker_list_data`;
        return this.http.post<IAppResponse<IKeyPair<IObjectListResponse[]>>>(url, request);
    }

    removeObjectGeometry(id: string): Observable<IAppResponse> {
        const url = `${this.apiPrefix}factor/front_row/delete_from_map`;
        return this.http.post<IAppResponse>(url, { id: id });
    }

    fetchMyMarkers(identify: string, checkedLayers: number[]): Observable<IMarkerResponse> {
        const url = `${this.apiPrefix}layers/layers_api/get_info_my_dynamic`;
        return this.http.post<IMarkerResponse>(url, { identify, checkedLayers });
    }

    fetchPublicMarkers(identify: string, checkedLayers: number[]): Observable<IMarkerResponse> {
        const url = `${this.apiPrefix}layers/layers_api/get_info_my_dynamic`;
        return this.http.post<IMarkerResponse>(url, { identify, checkedLayers });
    }

    fetchRegisterList(): Observable<{ id: number; name: string }[]> {
        const url = `${this.apiPrefix}factor/get_map_list_reestr`;
        return this.http.get<{ id: number; name: string }[]>(url);
    }

    fetchCoordinateSystems(): Observable<ICoordinateSystemResponse> {
        const url = `${this.apiPrefix}coordinate_setting_system/get_data_for_select`;
        return this.http.get<ICoordinateSystemResponse>(url);
    }

    fetchSettingsForMap(): Observable<IAppResponse<IMapSettings>> {
        const url = `${this.apiPrefix}map/getSettingsForMap`;
        return this.http.get<IAppResponse<IMapSettings>>(url);
    }

    renderMapFile(request: IRenderMapFileRequest): Observable<IRenderMapFileResponse> {
        const url = `${this.apiPrefix}layers/layers_api/render_map_file`;
        return this.http.post<IRenderMapFileResponse>(url, request);
    }

    getCoordsByCN(request: IGetCoordsByCN): Observable<number[]> {
        const url = `${this.apiPrefix}map/map_action/getCoordsByCN`;
        return this.http.post<number[]>(url, request);
    }

    getFiasData(request: IGetFiasDataRequest): Observable<IGetFiasDataResponse> {
        const url = `${this.apiPrefix}fias/getData`;
        return this.http.post<IGetFiasDataResponse>(url, request);
    }
}
